/**
 * Tenant → API Keys (Enterprise · `api_access` flag)
 *
 * Generate Bearer-token API keys, view usage, revoke. The secret is shown
 * exactly once at issue time — copy-and-go.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFeatures } from "@/context/FeaturesContext";
import { Plus, Trash2, Copy, KeyRound, Lock, BookOpen, Activity, AlertTriangle } from "lucide-react";
import Modal, { GoldButton, GhostButton, PageHeader, Field, Input } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const ALL_SCOPES = [
    { value: "clients:read", label: "clients:read — list & view clients" },
    { value: "cases:read", label: "cases:read — list & view cases" },
];

export default function ApiKeysPage() {
    const { flags } = useFeatures();
    const [keys, setKeys] = useState([]);
    const [creating, setCreating] = useState(null);
    const [usage, setUsage] = useState(null);
    const [revealed, setRevealed] = useState(null);

    const load = useCallback(async () => {
        try { const { data } = await api.get("/tenant/api-keys"); setKeys(data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { if (flags.api_access) load(); }, [flags.api_access, load]);

    const tableCtrl = useTableControls(keys, {
        accessors: {
            scopes: (k) => (k.scopes || []).join(" "),
            status: (k) => k.revoked_at ? "revoked" : "active",
        },
    });

    if (!flags.api_access) return <FlagDisabled/>;

    const create = async () => {
        try {
            const { data } = await api.post("/tenant/api-keys", creating);
            setRevealed(data); setCreating(null); load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const revoke = async (k) => {
        if (!window.confirm(`Revoke "${k.name}"? Any system using this key will stop working immediately.`)) return;
        try { await api.delete(`/tenant/api-keys/${k.id}`); toast.success("Key revoked"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const showUsage = async (k) => {
        try { const { data } = await api.get(`/tenant/api-keys/${k.id}/usage`); setUsage({ key: k, rows: data }); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader title="API Keys" subtitle="Developer access to your Law OS data via REST API. Bearer-token authenticated · scoped permissions · usage-logged · revocable." action={
                <GoldButton onClick={() => setCreating({ name: "", scopes: ["clients:read", "cases:read"] })} data-testid="new-key">
                    <Plus className="w-4 h-4 inline mr-1"/>New API key
                </GoldButton>
            }/>

            <DocsCard/>

            {keys.length === 0 ? (
                <div className="border border-dashed border-border py-20 text-center" data-testid="empty-keys">
                    <KeyRound className="w-8 h-8 mx-auto text-border mb-3"/>
                    <div className="font-serif text-xl mb-1">No API keys yet</div>
                    <div className="text-sm text-muted-foreground">Mint one to start integrating Law OS with your other tools.</div>
                </div>
            ) : (
                <div className="border border-border overflow-x-auto" data-testid="keys-table">
                    <FilterChip ctrl={tableCtrl} className="m-3"/>
                    <table className="w-full text-sm">
                        <thead className="bg-muted">
                            <tr>
                                <SortableTh field="name" ctrl={tableCtrl}>Name</SortableTh>
                                <SortableTh field="display_prefix" ctrl={tableCtrl}>Key prefix</SortableTh>
                                <SortableTh field="scopes" ctrl={tableCtrl}>Scopes</SortableTh>
                                <SortableTh field="created_at" ctrl={tableCtrl}>Created</SortableTh>
                                <SortableTh field="last_used_at" ctrl={tableCtrl}>Last used</SortableTh>
                                <SortableTh field="status" ctrl={tableCtrl}>Status</SortableTh>
                                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableCtrl.rows.map(k => (
                                <tr key={k.id} className="border-t border-border" data-testid={`key-${k.id}`}>
                                    <td className="px-3 py-2 font-medium">{k.name}</td>
                                    <td className="px-3 py-2 font-mono text-[11px] text-primary">{k.display_prefix}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex flex-wrap gap-1">{k.scopes.map(s => (
                                            <span key={s} className="text-[10px] font-mono px-2 py-0.5 bg-card border border-border">{s}</span>
                                        ))}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">
                                        {new Date(k.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}<br/>
                                        <span className="text-[10px] text-muted-foreground">{k.created_by}</span>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—"}</td>
                                    <td className="px-3 py-2">
                                        {k.revoked_at
                                            ? <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">revoked</span>
                                            : <span className="text-[10px] uppercase tracking-[0.1em] text-emerald-400">active</span>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2">
                                            <button onClick={() => showUsage(k)} data-testid={`usage-${k.id}`}
                                                className="text-[10px] uppercase tracking-[0.1em] text-primary hover:text-amber-300">
                                                <Activity className="w-3 h-3 inline"/> Usage
                                            </button>
                                            {!k.revoked_at && (
                                                <button onClick={() => revoke(k)} data-testid={`revoke-${k.id}`}
                                                    className="text-[10px] uppercase tracking-[0.1em] text-rose-400 hover:text-rose-300">
                                                    <Trash2 className="w-3 h-3 inline"/> Revoke
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {creating && (
                <Modal open={true} onClose={() => setCreating(null)} title="Create API key" size="md" testId="create-key-modal">
                    <div className="space-y-4">
                        <Field label="Name" hint="A label so you know which integration uses this key">
                            <Input value={creating.name} onChange={e => setCreating({...creating, name: e.target.value})}
                                placeholder="e.g. Zapier production" data-testid="key-name"/>
                        </Field>
                        <Field label="Scopes">
                            {ALL_SCOPES.map(s => (
                                <label key={s.value} className="flex items-center gap-2 py-1 text-sm" data-testid={`scope-${s.value}`}>
                                    <input type="checkbox" checked={creating.scopes.includes(s.value)}
                                        onChange={e => {
                                            const next = e.target.checked
                                                ? [...creating.scopes, s.value]
                                                : creating.scopes.filter(x => x !== s.value);
                                            setCreating({...creating, scopes: next});
                                        }}/>
                                    <code className="text-primary text-xs">{s.value}</code>
                                    <span className="text-xs text-muted-foreground">— {s.label.split(" — ")[1]}</span>
                                </label>
                            ))}
                        </Field>
                        <div className="flex justify-end gap-2 pt-2 border-t border-border">
                            <GhostButton onClick={() => setCreating(null)}>Cancel</GhostButton>
                            <GoldButton onClick={create} disabled={!creating.name.trim()} data-testid="confirm-create-key">Create key</GoldButton>
                        </div>
                    </div>
                </Modal>
            )}

            {revealed && <RevealedKeyModal data={revealed} onClose={() => setRevealed(null)}/>}
            {usage && <UsageModal usage={usage} onClose={() => setUsage(null)}/>}
        </>
    );
}

function RevealedKeyModal({ data, onClose }) {
    const copy = () => { navigator.clipboard.writeText(data.secret_once); toast.success("Copied to clipboard"); };
    return (
        <Modal open={true} onClose={onClose} title="API key created" size="md" testId="reveal-modal">
            <div className="space-y-4">
                <div className="border border-amber-700/40 bg-amber-950/20 p-3 flex items-start gap-2 text-xs">
                    <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0"/>
                    <div className="text-amber-100">{data.warning}</div>
                </div>
                <div className="border border-primary/40 bg-primary/5 p-3" data-testid="revealed-secret">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Secret</div>
                    <div className="flex items-center gap-2">
                        <code className="text-primary text-sm font-mono flex-1 break-all">{data.secret_once}</code>
                        <button onClick={copy} className="px-2 py-1 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.1em]" data-testid="copy-secret">
                            <Copy className="w-3 h-3 inline"/> Copy
                        </button>
                    </div>
                </div>
                <div className="flex justify-end">
                    <GoldButton onClick={onClose} data-testid="reveal-done">I've saved it</GoldButton>
                </div>
            </div>
        </Modal>
    );
}

function UsageModal({ usage, onClose }) {
    return (
        <Modal open={true} onClose={onClose} title={`Usage — ${usage.key.name}`} size="lg" testId="usage-modal">
            {usage.rows.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">No requests yet. Try a call:
                    <pre className="text-[11px] font-mono bg-background border border-border p-2 mt-2 text-left">curl -H "Authorization: Bearer YOUR_KEY" {process.env.REACT_APP_BACKEND_URL}/api/v1/whoami</pre>
                </div>
            ) : (
                <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-muted sticky top-0">
                            <tr>{["When","Method","Path","IP"].map(h => (
                                <th key={h} className="text-left px-3 py-2 font-medium uppercase tracking-wider">{h}</th>
                            ))}</tr>
                        </thead>
                        <tbody>{usage.rows.map((r,i) => (
                            <tr key={i} className="border-t border-border">
                                <td className="px-3 py-2 text-muted-foreground">{new Date(r.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}</td>
                                <td className="px-3 py-2 font-mono text-primary">{r.method}</td>
                                <td className="px-3 py-2 font-mono">{r.path}</td>
                                <td className="px-3 py-2 font-mono text-muted-foreground">{r.ip || "—"}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
}

function DocsCard() {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return (
        <div className="border border-primary/30 bg-primary/5 p-5 mb-6" data-testid="docs-card">
            <div className="flex items-center gap-2 mb-3"><BookOpen className="w-4 h-4 text-primary"/><div className="text-sm font-medium">Quickstart</div></div>
            <div className="text-xs text-muted-foreground mb-3">Authenticate every request with a Bearer header. Keys are tenant-scoped — they only return data for your firm.</div>
            <pre className="text-[11px] font-mono bg-background border border-border p-3 overflow-x-auto whitespace-pre">
{`# Verify your key
curl -H "Authorization: Bearer lawos_live_…" \\
  ${backend}/api/v1/whoami

# List clients (requires scope: clients:read)
curl -H "Authorization: Bearer lawos_live_…" \\
  ${backend}/api/v1/clients

# List cases (requires scope: cases:read)
curl -H "Authorization: Bearer lawos_live_…" \\
  ${backend}/api/v1/cases`}
            </pre>
            <div className="text-[10px] text-muted-foreground mt-3">Rate limit · 60 req/min per key. Failed auth returns 401. Revoked keys return 401. Plan downgrade that drops <code className="text-primary">api_access</code> immediately blocks all keys (403).</div>
        </div>
    );
}

function FlagDisabled() {
    return (
        <>
            <PageHeader title="API Keys" subtitle="Developer access to your Law OS data via REST API."/>
            <div className="border border-border p-8 text-center" data-testid="flag-disabled">
                <Lock className="w-8 h-8 mx-auto text-border mb-3"/>
                <div className="font-serif text-xl mb-2">Developer API access — Enterprise plan</div>
                <div className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
                    Generate scoped Bearer-token API keys for integrating Law OS with Zapier, n8n, your CRM, custom workflows. Available on the Enterprise plan.
                </div>
                <a href="/app/billing/plans" data-testid="upgrade-link"
                    className="inline-block px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.15em]">
                    Compare plans · Upgrade →
                </a>
            </div>
        </>
    );
}
